/**
 * Privacy-conscious measurement bridge for Don Bravo Brands.
 *
 * This file does not load an analytics vendor and never reads contact fields,
 * email addresses, search terms, or other visitor-entered text. It emits
 * high-level commerce and lead events that Shopify Customer Events or an
 * approved tag manager can consume only with explicit analytics and marketing
 * consent, permitted sharing, and no Global Privacy Control opt-out.
 */
(() => {
  const EVENT_PREFIX = "don_bravo_";

  function measurementAllowed() {
    try {
      if (navigator.globalPrivacyControl === true) return false;
      const privacy = window.Shopify?.customerPrivacy;
      const consent = privacy?.currentVisitorConsent?.();
      return consent?.analytics === "yes" && consent?.marketing === "yes" &&
        privacy.analyticsProcessingAllowed() === true &&
        privacy.marketingAllowed() === true && privacy.saleOfDataAllowed() === true;
    } catch (_error) {
      return false;
    }
  }

  function publish(name, detail = {}) {
    if (!measurementAllowed()) return;
    const payload = {
      event: `${EVENT_PREFIX}${name}`,
      page_path: window.location.pathname,
      ...detail,
    };

    document.dispatchEvent(new CustomEvent("donbravo:measurement", { detail: payload }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  }

  function safeDestination(link) {
    try {
      const url = new URL(link.href, window.location.origin);
      return `${url.pathname}${url.hash}`;
    } catch (_error) {
      return "";
    }
  }

  function classifyLink(link) {
    const destination = safeDestination(link);

    if (/\/pages\/(wholesale-hospitality|retail-distribution|venues|request-a-sample)/.test(destination)) {
      return "business_cta_click";
    }
    if (/\/(products|collections)\/.*liquid-snow/.test(destination)) {
      return "liquid_snow_cta_click";
    }
    if (destination === "/cart" || link.matches("[data-cart-toggle]")) {
      return "cart_open";
    }
    if (destination.includes("#db-join-circle")) {
      return "newsletter_cta_click";
    }

    return "";
  }

  document.addEventListener("click", (event) => {
    if (!measurementAllowed()) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest("a[href]");
    if (!link) return;

    const eventName = link.dataset.dbTrack || classifyLink(link);
    if (!eventName) return;

    publish(eventName, { destination: safeDestination(link) });
  });

  document.addEventListener("submit", (event) => {
    if (!measurementAllowed()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute("action") || "";

    if (action.includes("/cart/add")) {
      const variantId = form.querySelector('[name="id"]')?.value || "";
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      publish("add_to_cart_submit", {
        variant_id: variantId,
        quantity: Number.isFinite(quantity) ? quantity : 1,
      });
      return;
    }

    if (form.id === "dbn-newsletter") {
      publish("newsletter_submit");
      return;
    }

    if (form.matches(".dbt-form")) {
      const audience = form.querySelector('[name="contact[Audience]"]')?.value || "Business";
      publish("business_inquiry_submit", {
        form_context: window.location.pathname,
        lead_audience: audience,
      });
      return;
    }

    if (form.id === "dbv-contact-form") {
      publish("business_inquiry_submit", {
        form_context: window.location.pathname,
        lead_audience: "Hospitality and venue",
      });
      return;
    }

    if (form.matches(".dbc-form")) {
      publish("support_form_submit", { form_context: window.location.pathname });
      return;
    }

    if (form.matches('[action*="/contact"]')) {
      publish("lead_form_submit", { form_context: window.location.pathname });
      return;
    }

    if (action.includes("/search")) {
      publish("site_search_submit");
    }
  });

  document.addEventListener("on:cart:loaded", (event) => {
    if (!measurementAllowed()) return;
    const cart = event.detail?.cart;
    const operation = event.detail?.type;
    if (!cart || operation === "REFRESH") return;

    publish("cart_updated", {
      operation: operation || "UNKNOWN",
      item_count: Number(cart.item_count || 0),
      cart_value_minor_units: Number(cart.total_price || 0),
      currency: cart.currency || "USD",
    });
  });
})();
